import React, { useEffect, useMemo, useState } from 'react';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const fetchMotivos = async () => {
  const response = await fetch('/api/motivos-ajuste/active');
  if (!response.ok) {
    throw new Error(`Error ${response.status}`);
  }
  const payload = await response.json();
  return payload.motivos || [];
};

const CategoryAdjustmentModal = ({ open, category, onClose, onApply }) => {
  const [motivos, setMotivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchMotivos();
        if (!cancelled) {
          setMotivos(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'No se pudieron cargar los motivos.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    if (!motivos.length) {
      load();
    }
    return () => {
      cancelled = true;
    };
  }, [open, motivos.length]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const map = {};
    const adjustments = category?.adjustmentDetails?.adjustmentsByMotivo || [];
    adjustments.forEach((item) => {
      const value = parseFloat(item.value);
      if (!Number.isNaN(value) && value !== 0) {
        map[item.motivoId] = value;
      }
    });
    setValues(map);
  }, [open, category]);

  const total = useMemo(() => Object.values(values).reduce((sum, raw) => sum + (parseFloat(raw) || 0), 0), [values]);

  useLucideIcons([open, motivos.length]);

  const handleChange = (motivoId, raw) => {
    setValues((prev) => ({
      ...prev,
      [motivoId]: raw
    }));
  };

  const handleApply = () => {
    if (!category) {
      return;
    }
    const adjustments = Object.entries(values)
      .map(([motivoId, value]) => ({
        motivoId,
        value: parseFloat(value) || 0,
        motivoNombre: motivos.find((m) => String(m.id) === String(motivoId))?.nombre || 'Motivo'
      }))
      .filter((item) => item.value !== 0);
    onApply?.({
      categoryKey: category.key,
      categoryType: category.categoryType || (category.type || 'category'),
      categoryName: category.name,
      adjustments
    });
    onClose?.();
  };

  if (!open || !category) {
    return null;
  }

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content ux-modal" style={{ maxWidth: '720px' }}>
        <div className="ux-modal-header">
          <div className="ux-header-content">
            <h2>Ajustar pronóstico - {category.name}</h2>
            <p className="ux-subtitle">Define ajustes porcentuales por motivo</p>
          </div>
          <button className="ux-close-btn" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <div className="ux-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {loading && <p>Cargando motivos...</p>}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {!loading && motivos.map((motivo) => (
            <div className="ux-motivo-row" key={motivo.id}>
              <div className="ux-motivo-info">
                <div className="ux-motivo-icon">
                  <i data-lucide={motivo.icono || 'wrench'} />
                </div>
                <div className="ux-motivo-details">
                  <h4 className="ux-motivo-name">{motivo.nombre}</h4>
                  {motivo.descripcion ? <p className="ux-motivo-description">{motivo.descripcion}</p> : null}
                </div>
              </div>
              <div className="ux-input-container">
                <input
                  type="number"
                  step="0.1"
                  className="ux-adjustment-input"
                  value={values[motivo.id] ?? ''}
                  placeholder="0"
                  onChange={(event) => handleChange(motivo.id, event.target.value)}
                />
                <span className="ux-percentage-symbol">%</span>
              </div>
            </div>
          ))}
        </div>
        <div className="ux-modal-footer">
          <div className="ux-footer-left">
            <div className={`ux-total-value ${total > 0 ? 'ux-positive' : total < 0 ? 'ux-negative' : 'ux-neutral'}`}>
              Total: {total > 0 ? '+' : ''}{total.toFixed(1)}%
            </div>
          </div>
          <div className="ux-footer-right">
            <button type="button" className="ux-btn ux-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="ux-btn ux-btn-primary" onClick={handleApply} disabled={loading}>
              Aplicar ajuste
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryAdjustmentModal;
